import { Page } from '@/lib/types';

import FaqPageListingService from '@/features/faq-page/services/faq-page-listing.service';
import { FaqPageListingMap } from '@/features/faq-page/type';
import navigationTransformer from '@/hygraph/transformers/navigation.transformer';
import DisciplinesService, { Discipline } from '@/services/disciplines.service';
import HighPayingJobsService, {
  HighPayingJobsResponse,
} from '@/services/high-paying-jobs.service';
import JobCategoryPageRoutesService from '@/services/job-category-page-routes.service';
import { TNavbarItem } from '@/services/navigation.service';
import PopularDestinationsService, {
  PopularDestinationsResponse,
} from '@/services/popular-destinations.service';
import PopularLocationsService, {
  PopularLocationsResponse,
} from '@/services/popular-locations.service';
import PressesService, {
  PressListItemsResponse,
} from '@/services/presses.service';

// PageStaticDataProps is a type that represents the data that is fetched
// from the server and is used to hydrate the page. None of this data can be undefined, only null.
// "Reason: `undefined` cannot be serialized as JSON. Please use `null` or omit this value."
export type PageStaticDataProps = {
  popularDestinations: PopularDestinationsResponse;
  highPayingJobs: HighPayingJobsResponse;
  popularLocations: PopularLocationsResponse;
  presses: PressListItemsResponse;
  faqPageListings: FaqPageListingMap;
  disciplines: Discipline[];
  navbarNavigation: TNavbarItem[];
  footerNavigation: TNavbarItem[];
  jobCategoryPageRoutes: Record<string, string>;
};

export const INITIAL_PAGE_STATIC_DATA_PROPS: PageStaticDataProps = {
  popularDestinations: [],
  highPayingJobs: [],
  popularLocations: [],
  presses: {},
  faqPageListings: {},
  disciplines: [],
  navbarNavigation: [],
  footerNavigation: [],
  jobCategoryPageRoutes: {},
};

const get = async (page: Page): Promise<PageStaticDataProps> => {
  const [
    popularDestinations,
    highPayingJobs,
    popularLocations,
    presses,
    faqPageListings,
    disciplines,
  ] = await Promise.all([
    PopularDestinationsService.get(page),
    HighPayingJobsService.get(page),
    PopularLocationsService.get(page),
    PressesService.getByPage(page),
    FaqPageListingService.getByPage(page),
    DisciplinesService.get(),
  ]);

  const jobCategoryPageRoutes = await JobCategoryPageRoutesService.getByPage(
    page,
    disciplines
  );

  const { footer, navigation } = page?.templateSetting || {};
  return {
    popularDestinations,
    highPayingJobs,
    popularLocations,
    presses,
    faqPageListings,
    disciplines,
    navbarNavigation: navigationTransformer(navigation?.nav),
    footerNavigation: navigationTransformer(footer?.nav),
    jobCategoryPageRoutes,
  };
};

const PageStaticDataService = {
  get,
};

export default PageStaticDataService;
